import React, { useEffect, useRef, useState } from 'react';
import ChatBadges from './ChatBadges';
import LobbyUserAvatar from './LobbyUserAvatar';

/**
 * Compact "who's at the table" control for the felt.
 * Eye when any watcher is present; silhouette when only seated players.
 */
function TablePresenceButton({
  users = [],
  count = 0,
  currentUserId,
  canModerate,
  onUserClick,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const hasWatcher = users.some((u) => u.isWatching);
  const icon = hasWatcher ? '👁' : '👤';
  const label = hasWatcher
    ? `${count} at table (watchers present)`
    : `${count} at table`;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="table-presence" ref={rootRef}>
      <button
        type="button"
        className={[
          'table-presence-btn',
          hasWatcher ? 'table-presence-btn--watchers' : '',
          open ? 'table-presence-btn--open' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => setOpen((v) => !v)}
        title={label}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="table-presence-icon" aria-hidden="true">{icon}</span>
        <span className="table-presence-count">{count}</span>
      </button>

      {open && (
        <div className="table-presence-panel" role="dialog" aria-label="People at this table">
          <div className="table-presence-panel-head">
            <strong>At table</strong>
            <span className="table-presence-panel-meta">{count}</span>
          </div>
          <ul className="table-presence-list">
            {users.length === 0 && (
              <li className="table-presence-empty">Nobody listed yet</li>
            )}
            {users.map((u) => {
              const isSelf = currentUserId != null
                && Number(u.userId) === Number(currentUserId);
              const modClickable = canModerate && !isSelf && u.userId != null;
              const content = (
                <>
                  <LobbyUserAvatar
                    username={u.username}
                    avatarUrl={u.avatarUrl}
                    isHost={!!u.isHost}
                  />
                  <ChatBadges roleBadge={u.roleBadge} clubBadge={u.clubBadge} />
                  <span className="table-presence-name">{u.username}</span>
                  {u.isAfk ? (
                    <span className="table-presence-tag table-presence-tag--afk" title="Away">AFK</span>
                  ) : null}
                  {u.isWatching ? (
                    <span className="table-presence-tag" aria-label="Watching">👁</span>
                  ) : u.seat ? (
                    <span className="table-presence-tag table-presence-tag--seat">{u.seat}</span>
                  ) : null}
                </>
              );
              return (
                <li key={String(u.userId ?? u.username)}>
                  {modClickable ? (
                    <button
                      type="button"
                      className="table-presence-user-btn"
                      onClick={(e) => {
                        onUserClick?.(u, e.currentTarget.getBoundingClientRect());
                        setOpen(false);
                      }}
                    >
                      {content}
                    </button>
                  ) : (
                    <span className="table-presence-user-line">{content}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default TablePresenceButton;
